"use client"

import { Button } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { useMutation } from "@apollo/client"
import { useState } from "react"
import {
  AcceptConnectionRequestDocument,
  RejectConnectionRequestDocument,
  CancelConnectionRequestDocument,
  CreateConnectionRequestDocument,
  UserConnectionStatus,
} from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { MessageModal } from "../Messaging/MessageModal"
import { ampli } from "@/ampli"
import clsx from "clsx"
import { UserAddIcon } from "@/icons/outline/UserAddIcon"
import { ClockIcon } from "@heroicons/react/24/outline"

const ConnectOrMessageButtonFragment = graphql(`
  fragment ConnectOrMessageButton on User {
    id
    uuid
    viewerConnectionStatus
    viewerSentConnectionRequest {
      id
    }
    viewerReceivedConnectionRequest {
      id
    }
    ...MessageModal
  }
`)

interface Props {
  user: FragmentType<typeof ConnectOrMessageButtonFragment> | undefined | null
  serverSession: NovaSession
  layout?: "row" | "column"
  trackingEntryPoint: string
  className?: string
  fillSpace?: boolean
}

const ConnectIconPurple = () => <UserAddIcon className="stroke-primary-5" />
const ConnectIconWhite = () => <UserAddIcon className="stroke-bg-1" />
const PendingIcon = () => <ClockIcon className="size-[22px] stroke-primary-5" />

export const ConnectOrMessageButton: React.FC<Props> = props => {
  const user = useFragment(ConnectOrMessageButtonFragment, props.user)
  const { serverSession, layout = "column", className, fillSpace = false, trackingEntryPoint } = props

  const viewerIsClient = serverSession?.account === "client"

  const [cancelRequest] = useMutation(CancelConnectionRequestDocument)
  const [acceptRequest] = useMutation(AcceptConnectionRequestDocument)
  const [createRequest] = useMutation(CreateConnectionRequestDocument)
  const [rejectRequest] = useMutation(RejectConnectionRequestDocument)

  const [isLoading1, setIsLoading1] = useState(false)
  const [isLoading2, setIsLoading2] = useState(false)

  const receivedRequestId = user?.viewerReceivedConnectionRequest?.id
  const sentRequestId = user?.viewerSentConnectionRequest?.id

  const handleConnect = async () => {
    setIsLoading1(true)
    ampli.connectionRequestSend({
      entry_point: trackingEntryPoint,
    })
    await createRequest({
      variables: {
        userId: user?.id!,
      },
      optimisticResponse: {
        createConnectionRequest: {
          __typename: "CreateConnectionRequest",
          success: true,
          user: {
            id: user?.id!,
            viewerConnectionStatus: UserConnectionStatus.ConnectionRequestSent,
          },
        },
      },
      update: cache => {
        cache.modify({
          id: cache.identify(user!),
          fields: {
            viewerConnectionStatus: () => UserConnectionStatus.ConnectionRequestSent,
          },
        })
      },
    })
    setIsLoading1(false)
  }
  const handleAccept = async () => {
    setIsLoading1(true)
    ampli.connectionRequestAccept({
      entry_point: "connectOrMessageButton",
    })
    if (receivedRequestId) {
      await acceptRequest({
        variables: {
          id: receivedRequestId!,
        },
        optimisticResponse: {
          acceptConnectionRequest: {
            __typename: "AcceptConnectionRequest",
            success: true,
            user: {
              id: user?.id!,
              viewerConnectionStatus: UserConnectionStatus.Connected,
            },
          },
        },
        update: cache => {
          cache.modify({
            id: cache.identify(user!),
            fields: {
              viewerConnectionStatus: () => UserConnectionStatus.Connected,
            },
          })
          setIsLoading1(false)
        },
      })
    }
  }
  const handleCancelRequest = async () => {
    setIsLoading1(true)
    if (sentRequestId) {
      await cancelRequest({
        variables: {
          id: sentRequestId!,
        },
        optimisticResponse: {
          cancelConnectionRequest: {
            __typename: "CancelConnectionRequest",
            success: true,
            user: {
              id: user?.id!,
              viewerConnectionStatus: UserConnectionStatus.NotConnected,
            },
          },
        },
        update: cache => {
          cache.modify({
            id: cache.identify(user!),
            fields: {
              viewerConnectionStatus: () => UserConnectionStatus.NotConnected,
            },
          })
        },
      })
    }
    setIsLoading1(false)
  }
  const handleIgnore = async () => {
    setIsLoading2(true)

    if (receivedRequestId) {
      await rejectRequest({
        variables: { id: receivedRequestId! },
        optimisticResponse: {
          rejectConnectionRequest: {
            __typename: "RejectConnectionRequest",
            success: true,
            user: {
              id: user?.id!,
              viewerConnectionStatus: UserConnectionStatus.NotConnected,
            },
          },
        },
        update: cache => {
          cache.modify({
            id: cache.identify(user!),
            fields: {
              viewerConnectionStatus: () => UserConnectionStatus.NotConnected,
            },
          })
        },
      })
    }
    setIsLoading2(false)
  }

  // Client accounts can't connect, they can always message instead
  if (viewerIsClient) {
    return <MessageModal user={user} serverSession={serverSession} trackingEntryPoint={trackingEntryPoint} />
  }

  switch (user?.viewerConnectionStatus) {
    case UserConnectionStatus.ConnectionRequestReceived:
      return (
        <div className={clsx("flex gap-2 sm:gap-3", fillSpace && "flex-1", layout === "row" ? "flex-row" : "flex-col")}>
          <div className={clsx("flex md:hidden", fillSpace && "flex-1")}>
            <Button
              variant="secondary"
              onClick={handleAccept}
              loading={isLoading1}
              LeftElement={ConnectIconPurple}
              className={className}>
              Accept
            </Button>
          </div>
          <div className={clsx("hidden md:flex", fillSpace && "flex-1")}>
            <Button
              variant="secondary"
              onClick={handleAccept}
              loading={isLoading1}
              LeftElement={ConnectIconPurple}
              className={className}>
              Accept request
            </Button>
          </div>
          <Button variant="tertiary" onClick={handleIgnore} loading={isLoading2} className={className}>
            Decline
          </Button>
        </div>
      )
    case UserConnectionStatus.ConnectionRequestSent:
      return (
        <Button
          variant="secondary"
          LeftElement={PendingIcon}
          onClick={handleCancelRequest}
          loading={isLoading1}
          className={className}>
          Pending
        </Button>
      )
    case UserConnectionStatus.NotConnected:
      return (
        <Button
          variant="primary"
          onClick={handleConnect}
          loading={isLoading1}
          LeftElement={ConnectIconWhite}
          className={className}>
          Connect
        </Button>
      )
    default:
      return <MessageModal user={user} serverSession={serverSession} trackingEntryPoint={trackingEntryPoint} />
  }
}
